import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { KeretService } from '../keret.service';

/**
 *  LICENC (névjegy-jellegű állapotlap, minden felhasználónak)
 *
 *  Két oszlop, ahogy a víruskeresők „Előfizetés / Telepített alkalmazás” lapja (user, 2026-11-05):
 *  bal oldalt az előfizetés (előfizető, állapot, lejárat, azonosító, csomag, modulok), jobb oldalt a
 *  telepített program (név, verzió, újdonságok, hibaelhárítás). Fölötte egy sáv a teljes állapotról.
 *
 *  Az adat elsősorban a `/api/licenc-allapot` (licenc-kliens.js › allapotReszletes) – ugyanaz, amit az
 *  admin Licenc-aktiválás (/admin/licenc) mutat. Ahol még nincs licenckulcs megadva, a régi aktiválás
 *  (`/api/license` › `activation` tábla: jogosult, csomag, lejárat, activated 0/1/2) adja az adatokat.
 *  A verzió-frissességet a `/api/frissites/jelzes` adja (csak adminnak jelez új verziót).
 */
type Allapot = 'aktiv' | 'frissitheto' | 'figyelem' | 'hiba' | 'ismeretlen';

@Component({
  selector: 'app-license',
  templateUrl: './license.component.html',
  styleUrls: ['./license.component.scss'],
})
export class LicenseComponent implements OnInit {
  betolt = true;
  allapot: any = null;
  regi: any = null;
  hiba = false;
  ujVerzio: string | null = null;
  nevjegy = false;

  readonly fiokUrl = 'https://cronomit.org/fiok';

  constructor(private http: HttpClient, private keret: KeretService) {}

  async ngOnInit() {
    const intez = this.keret.a?.intezmenyId?.() || '';
    const mod = this.keret.a?.aktualisMod?.() || '';
    try {
      this.allapot = await firstValueFrom(this.http.get<any>('/api/licenc-allapot'));
    } catch {
      this.hiba = true;
    }
    try {
      const r: any = await firstValueFrom(this.http.get('/api/license'));
      this.regi = Array.isArray(r) && r[0] ? r[0] : null;
    } catch { /* régi aktiválás nincs */ }
    this.betolt = false;
    try {
      const r: any = await firstValueFrom(this.http.get(
        `/api/frissites/jelzes?intezmeny=${encodeURIComponent(intez)}${mod ? `&mode=${encodeURIComponent(mod)}` : ''}`));
      if (r?.van_ujitas && r?.legujabb) this.ujVerzio = r.legujabb;
    } catch { /* nincs kapcsolat – nem jelzünk */ }
  }

  get licenc(): any { return this.allapot?.licenc || null; }

  /** A régi (activation táblás) aktiválás – csak ha az új licencrendszerben nincs kulcs. */
  get regiAkt(): any { return !this.allapot?.aktiv && this.regi?.jogosult ? this.regi : null; }

  get modulok(): string[] {
    return Array.isArray(this.licenc?.modulok) ? this.licenc.modulok : [];
  }

  /** A teljes állapot egy szóban – ebből jön a sáv színe, ikonja, szövege. */
  get allapotKod(): Allapot {
    const r = this.regiAkt;
    if (r) {
      if (Number(r.activated) === 0 || this.regiLejart) return 'hiba';
      return Number(r.activated) === 2 || this.ujVerzio ? 'frissitheto' : 'aktiv';
    }
    if (this.hiba || !this.allapot) return 'ismeretlen';
    const a = this.allapot;
    if (a.mas_telepitesen || a.elavult || this.licenc?.ervenyes === false) return 'hiba';
    if (!a.aktiv || !this.licenc) return 'figyelem';
    return this.ujVerzio ? 'frissitheto' : 'aktiv';
  }

  get savIkon(): string {
    switch (this.allapotKod) {
      case 'aktiv': return 'checkmark-circle';
      case 'frissitheto': return 'arrow-up-circle';
      case 'hiba': return 'close-circle';
      default: return 'alert-circle';
    }
  }

  get savCim(): string {
    const a = this.allapot;
    switch (this.allapotKod) {
      case 'aktiv': return 'Az előfizetés aktív, a program naprakész';
      case 'frissitheto': return 'Az előfizetés aktív, új programverzió érhető el';
      case 'ismeretlen': return 'A licenc állapota nem kérdezhető le';
      case 'hiba':
        if (this.regiAkt) return this.regiLejart ? 'Az előfizetés lejárt' : 'A program aktiválása sikertelen';
        if (a?.mas_telepitesen) return 'A licenckulcs egy másik telepítésen van használatban';
        if (a?.elavult) return 'A licencszerver hosszabb ideje nem érhető el';
        return this.licenc?.statusz === 'lejart' ? 'Az előfizetés lejárt' : 'Az előfizetés fel van függesztve';
      default:
        return a?.aktiv ? 'A licenckulcs megadva, várakozás a licencszerverre' : 'A program még nincs aktiválva';
    }
  }

  get savSzoveg(): string {
    const a = this.allapot;
    switch (this.allapotKod) {
      case 'aktiv': return 'A program kapcsolódott a licencszerverhez, és a legfrissebb verziót használja.';
      case 'frissitheto': return this.ujVerzio
        ? `Elérhető verzió: ${this.ujVerzio}. A frissítést az Adminisztráció › Frissítés oldalon indíthatja.`
        : 'A program kapcsolódott a licencszerverhez, de nem a legfrissebb verziót használja.';
      case 'ismeretlen': return 'Ellenőrizze a hálózati kapcsolatot, majd töltse újra az oldalt.';
      case 'hiba':
        if (this.regiAkt && !this.regiLejart) return 'Ha ez hiba, kérjük, vegye fel a kapcsolatot a rendszergazdával.';
        if (a?.mas_telepitesen) return a?.uzenet || 'Egy kulcs egyszerre csak egy aktív telepítésen használható.';
        if (a?.elavult) return 'Az offline türelmi idő lejárt. Ellenőrizze a kiszolgáló internetkapcsolatát.';
        return 'Az előfizetés meghosszabbításához keresse a Cronomit ügyfélszolgálatát, vagy nyissa meg a fiókoldalt.';
      default:
        return a?.aktiv
          ? 'A licencszerver még nem adott választ. Ez néhány percig is eltarthat.'
          : 'A program addig is teljes funkcionalitással használható. A licenckulcsot az intézmény adminisztrátora adhatja meg.';
    }
  }

  get elofizetesAllapot(): string {
    const r = this.regiAkt;
    if (r) return this.regiLejart ? 'Lejárt' : Number(r.activated) === 0 ? 'Nincs aktiválva' : 'Aktív';
    if (!this.allapot) return '–';
    if (!this.allapot.aktiv) return 'Nincs aktiválva';
    if (this.allapot.mas_telepitesen) return 'Másik telepítésen';
    const l = this.licenc;
    if (!l) return 'Aktiválás folyamatban';
    if (l.statusz === 'lejart') return 'Lejárt';
    if (l.ervenyes === false) return 'Felfüggesztve';
    return 'Aktív';
  }

  get elofizeto(): string {
    return this.licenc?.nev || this.regiAkt?.jogosult || this.keret.a?.intezmenyNev?.() || 'Cronomit-előfizetés';
  }

  get csomag(): string {
    if (this.regiAkt?.name) return this.regiAkt.name;
    const t = this.licenc?.tipus;
    if (!t) return '';
    return t === 'cloud' ? 'Felhős' : 'Saját kiszolgálós';
  }

  get regiLejart(): boolean {
    const e = this.regiAkt?.expire;
    return !!e && new Date(e).getTime() < Date.now();
  }

  get lejarat(): string {
    const r = this.regiAkt;
    if (r) {
      if (!r.expire) return 'Nincs lejárat';
      const d = new Date(r.expire);
      return isNaN(d.getTime()) ? String(r.expire)
        : `${d.getFullYear()}. ${String(d.getMonth() + 1).padStart(2, '0')}. ${String(d.getDate()).padStart(2, '0')}.`;
    }
    const d = this.licenc?.ervenyes_ig;
    if (!d) return this.licenc ? 'Nincs lejárat' : '–';
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(d));
    return m ? `${m[1]}. ${m[2]}. ${m[3]}.` : String(d);
  }

  korlat(kulcs: string): string {
    const v = this.licenc?.korlatok?.[kulcs];
    return v == null ? 'korlátlan' : String(v);
  }

  get termek(): string {
    const mod = this.keret.a?.aktualisMod?.() || '';
    const cim = (this.keret.a?.modok?.() || []).find((m) => m.name === mod)?.cim;
    return cim ? `Cronomit ${cim}` : 'Cronomit';
  }

  get verzio(): string {
    return this.keret.a?.verzio?.() || environment.version || '–';
  }

  get utolsoKapcsolat(): string {
    const s = this.allapot?.utolso_szerver_kontakt;
    if (!s) return 'még nem volt';
    const d = new Date(s);
    return isNaN(d.getTime()) ? s : d.toLocaleString('hu-HU', { dateStyle: 'medium', timeStyle: 'short' });
  }

  get ev(): number { return new Date().getFullYear(); }
}
